import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useProfile } from "@/hooks/useAuth";
import { useAppStore } from "@/store/useAppStore";
import { useIsSolo } from "@/hooks/useIsSolo";
import { fetchProvisions } from "@/lib/queries";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useFormatCurrency } from "@/hooks/useFormatCurrency";
import { toast } from "sonner";
import { toastError } from "@/lib/errors";
import { Plus, Pencil, Archive, ArchiveRestore, Trash2 } from "lucide-react";
import { SavingsGoalCard } from "@/components/SavingsGoalCard";

export const Route = createFileRoute("/_app/provisions")({ component: ProvisionsPage });

function ProvisionsPage() {
  const formatCurrency = useFormatCurrency();
  const { profile } = useProfile();
  const hh = profile?.household_id;
  const { viewMode } = useAppStore();
  const { isSolo } = useIsSolo();
  const myId = profile?.id;
  const qc = useQueryClient();
  const provQ = useQuery({ queryKey: ["provisions", hh, "all"], queryFn: () => fetchProvisions(hh!, { includeArchived: true }), enabled: !!hh });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);
  const [name, setName] = useState("");
  const [target, setTarget] = useState("0");
  const [type, setType] = useState<"saving" | "provision">("provision");
  const [scope, setScope] = useState<"personal" | "household">("household");
  const [showArchived, setShowArchived] = useState(false);

  async function save() {
    if (!hh) return;
    const visibility = isSolo ? "shared" : (scope === "personal" ? "private" : "shared");
    const owner_id = isSolo ? null : (scope === "personal" ? profile?.id : null);
    if (editing) {
      const { error } = await supabase.from("provisions_and_goals").update({
        name, target_amount: parseFloat(target), type,
      }).eq("id", editing.id);
      if (error) return toastError(error);
      toast.success("Cajita actualizada");
    } else {
      const { error } = await supabase.from("provisions_and_goals").insert({
        household_id: hh, name, target_amount: parseFloat(target), type,
        visibility, owner_id,
      });
      if (error) return toastError(error);
      toast.success("Cajita creada");
    }
    qc.invalidateQueries({ queryKey: ["provisions"] });
    setOpen(false); setName(""); setTarget("0"); setScope("household"); setEditing(null);
  }

  function openNew() {
    setEditing(null); setName(""); setTarget("0"); setType("provision"); setScope("household"); setOpen(true);
  }
  function openEdit(p: any) {
    setEditing(p); setName(p.name); setTarget(String(p.target_amount));
    setType(p.type); setScope(p.visibility === "private" ? "personal" : "household"); setOpen(true);
  }

  async function deposit(id: string) {
    const v = window.prompt("Monto a aportar");
    if (!v) return;
    const parsed = parseFloat(v);
    if (isNaN(parsed) || parsed <= 0) {
      toast.error("Ingresa un monto válido.");
      return;
    }
    const cur = provQ.data?.find((p) => p.id === id);
    if (!cur) return;
    await supabase.from("provisions_and_goals").update({ current_balance: Number(cur.current_balance) + parsed }).eq("id", id);
    qc.invalidateQueries({ queryKey: ["provisions"] });
  }

  async function archive(p: any) {
    const { error } = await supabase.from("provisions_and_goals").update({ archived_at: new Date().toISOString() }).eq("id", p.id);
    if (error) return toastError(error);
    toast.success("Cajita archivada");
    qc.invalidateQueries({ queryKey: ["provisions"] });
  }

  async function unarchive(p: any) {
    const { error } = await supabase.from("provisions_and_goals").update({ archived_at: null }).eq("id", p.id);
    if (error) return toastError(error);
    toast.success("Cajita restaurada");
    qc.invalidateQueries({ queryKey: ["provisions"] });
  }

  async function remove(p: any) {
    // El borrado físico solo se permite si la cajita no tiene saldo ni movimientos.
    // El saldo lo chequeamos acá (UX rápida); la regla completa (movimientos del
    // hogar, incluidos los del par) la valida el servidor en delete_provision.
    if (Number(p.current_balance) !== 0) {
      toast.error("No se puede eliminar: la cajita tiene saldo. Archivala en su lugar.");
      return;
    }
    if (!window.confirm(`¿Eliminar "${p.name}"? Esta acción no se puede deshacer.`)) return;
    const { error } = await supabase.rpc("delete_provision", { _id: p.id });
    if (error) return toastError(error);
    toast.success("Cajita eliminada");
    qc.invalidateQueries({ queryKey: ["provisions"] });
  }

  const visible = (provQ.data ?? []).filter((p: any) => isSolo ? true : (
    viewMode === "personal"
      ? (p.visibility === "private" && p.owner_id === myId)
      : p.visibility === "shared"
  ));
  const activas = visible.filter((p: any) => !p.archived_at);
  const archivadas = visible.filter((p: any) => p.archived_at);

  const renderCard = (p: any, isArchived: boolean) => {
    const pct = Number(p.target_amount) > 0 ? Math.min(100, (Number(p.current_balance) / Number(p.target_amount)) * 100) : 0;
    const hasBalance = Number(p.current_balance) !== 0;
    return (
      <Card key={p.id} className={isArchived ? "opacity-60" : undefined}><CardContent className="p-4 space-y-2">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-semibold">{p.name}</div>
            <div className="text-xs text-muted-foreground">{p.type === "saving" ? "Ahorro" : "Provisión"}</div>
          </div>
          <div className="flex gap-1">
            {!isArchived && (
              <>
                <Button size="icon" variant="ghost" onClick={() => openEdit(p)} title="Editar"><Pencil className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" onClick={() => archive(p)} title="Archivar"><Archive className="h-4 w-4" /></Button>
                <Button size="sm" variant="outline" onClick={() => deposit(p.id)}>Aportar</Button>
              </>
            )}
            {isArchived && (
              <Button size="icon" variant="ghost" onClick={() => unarchive(p)} title="Restaurar"><ArchiveRestore className="h-4 w-4" /></Button>
            )}
            <Button size="icon" variant="ghost" onClick={() => remove(p)}
              title={hasBalance ? "Tiene saldo: archivala en vez de eliminar" : "Eliminar"}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <div className="text-sm">{formatCurrency(Number(p.current_balance))} / {formatCurrency(Number(p.target_amount))}</div>
        <Progress value={pct} />
      </CardContent></Card>
    );
  };

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold">Cajitas y Ahorros</h1>
      {/* Meta de ahorro del hogar (B2c). `key`: el mes elegido y el borrador del inicio se resetean al cambiar de sesión. */}
      {hh && myId && <SavingsGoalCard key={`${hh}:${myId}`} householdId={hh} profileId={myId} />}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Metas manuales</h2>
        <Button onClick={openNew}><Plus className="mr-2 h-4 w-4" /> Nueva</Button>
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {activas.map((p: any) => renderCard(p, false))}
      </div>
      {archivadas.length > 0 && (
        <div className="space-y-2">
          <button type="button" onClick={() => setShowArchived((s) => !s)}
            className="text-sm text-muted-foreground hover:text-foreground">
            {showArchived ? "▾" : "▸"} Archivadas ({archivadas.length})
          </button>
          {showArchived && (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {archivadas.map((p: any) => renderCard(p, true))}
            </div>
          )}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Editar cajita" : "Nueva cajita"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nombre</Label><Input value={name} onChange={(e) => setName(e.target.value)} /></div>
            <div><Label>Meta</Label><Input type="number" step="0.01" value={target} onChange={(e) => setTarget(e.target.value)} /></div>
            <div><Label>Tipo</Label>
              <Select value={type} onValueChange={(v: any) => setType(v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="provision">Provisión (gastos futuros)</SelectItem>
                  <SelectItem value="saving">Ahorro (objetivo)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {!isSolo && !editing && <div>
              <Label>¿Para quién es esta cajita?</Label>
              <div className="mt-2 grid grid-cols-2 gap-2">
                <button type="button" onClick={() => setScope("personal")}
                  className={"rounded-lg border-2 p-3 text-left transition " + (scope === "personal" ? "border-primary bg-primary/5" : "border-border hover:border-primary/50")}>
                  <div>🧍 Mi Gasto Personal</div>
                  <div className="text-xs text-muted-foreground">Solo tú la verás</div>
                </button>
                <button type="button" onClick={() => setScope("household")}
                  className={"rounded-lg border-2 p-3 text-left transition " + (scope === "household" ? "border-primary bg-primary/5" : "border-border hover:border-primary/50")}>
                  <div>🏠 Gasto del Hogar</div>
                  <div className="text-xs text-muted-foreground">Visible para tu pareja</div>
                </button>
              </div>
            </div>}
          </div>
          <DialogFooter><Button onClick={save}>Guardar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
